import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdStack, PdText } from "src/components/layout";

import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { tModalComponentProps } from "src/redux/modals/types";
import { postRetailerSetupTerms } from "src/util/apiNew";
import { openUrlInNewTab } from "src/util/linking";
import { navigationRef } from "src/RootNavigation";
import { useQuery } from "src/hooks/api";
import { useIsMobile } from "src/hooks/ui";

type tModalProps = tModalComponentProps<void>;

const Step2Qualification: React.FC<{ qualificationUrl: string }> = ({ qualificationUrl }) => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();

  const handleOnCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(qualificationUrl);
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const handleOnContinue = async () => {
    await openUrlInNewTab(qualificationUrl);
  };

  return (
    <>
      <PdText style={styles.mb3}>Please click continue and follow the prompts to complete your application.</PdText>
      <PdText style={styles.mb6}>
        If you have pop up blockers on and are not redirected to a new window tab, please copy and paste the link in a
        new browser window to complete your application.
      </PdText>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        isReversed={isMobile}
        layout={[1, 1]}
        gap={3}
        style={styles.wFull}
      >
        <PdButton onPress={handleOnCopyLink}>
          <PdButtonLabel>Copy Link</PdButtonLabel>
        </PdButton>
        <PdButton onPress={handleOnContinue}>
          <PdButtonLabel>Continue &gt;</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const SuccessFeedback: React.FC<Pick<tModalProps, "onResolve">> = ({ onResolve }) => {
  const handleOnNavigateToShop = () => {
    if (navigationRef.current) {
      navigationRef.current.navigate(kRoute.P99);
    }

    onResolve();
  };

  return (
    <>
      <View style={[styles.mb6, styles.alignCenter]}>
        <PdModalTitle style={[styles.mb5, styles.textCenter]}>Success!</PdModalTitle>
        <PdText style={[styles.mb3, styles.textCenter]}>Application Completed.</PdText>
        <PdText style={styles.textCenter}>
          Once approved, your available balance will display on your navigation menu.
        </PdText>
      </View>
      <PdButton style={styles.mlAuto} onPress={handleOnNavigateToShop}>
        <PdButtonLabel>Shop our Brands</PdButtonLabel>
      </PdButton>
    </>
  );
};

const ModalContent: React.FC<Pick<tModalProps, "onResolve">> = ({ onResolve }) => {
  const { isLoading, data } = useQuery(postRetailerSetupTerms);
  const rCompany = useSelector((state) => state.CompanyReducer);

  if (isLoading) {
    return <ActivityIndicator style={styles.modalLoading} color={kColors.brandDarkGreen} size="small" />;
  }

  if (
    rCompany.type === "retailer" &&
    data &&
    (rCompany.qualificationStatusForTerms === "approved" || rCompany.qualificationStatusForTerms === "declined")
  )
    return <SuccessFeedback onResolve={onResolve} />;

  if (!data) {
    return (
      <>
        <PdModalTitle style={styles.mb5}>Net Terms Application</PdModalTitle>
        <PdText>
          Your terms application has been submitted. If you&apos;d like to make any updates, please contact us via
          messages.
        </PdText>
      </>
    );
  }

  return (
    <>
      <PdModalTitle style={styles.mb5}>Net Terms Application</PdModalTitle>
      <Step2Qualification qualificationUrl={data} />
    </>
  );
};

const RetailerSetupTerms: React.FC<tModalProps> = ({ onClose, onResolve }) => {
  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} onClose={onClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <ModalContent onResolve={onResolve} />
      </ScrollView>
    </PdModal>
  );
};

const useRetailerSetupTerms = (): tUseModal<tModalProps> => {
  return useModal(RetailerSetupTerms);
};

export default useRetailerSetupTerms;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  mb6: {
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalLoading: {
    alignItems: "center",
    height: 250,
    justifyContent: "center",
    width: "100%",
  },
  textCenter: {
    textAlign: "center",
  },
  wFull: {
    width: "100%",
  },
});
